/**
 * TRAD-3921 — 인사이트 글의 «언어» 규칙(순수 함수만). 설계 = docs-ops/2026-11-05-insight-bilingual-design.md §3·§4-2,
 * 계약 = docs-ops/2026-11-05-insight-bilingual-contract.md §1.
 *
 * 한 줄 규칙: **주소가 글의 언어를 정한다.** `/insights…` = 한국어판 · `/en/insights…` = 영어판.
 * 예외 하나: 앱 웹뷰(`?embed=1`)는 주소를 바꾸지 않고 기기 언어로 고른다(공유도 색인도 안 되는 주소 — 앱을 안 고쳐도 영어판이 열린다).
 *
 * 응답 쪽: `lang` = 서버가 «실제로» 내려 준 글자의 언어 · `availableLangs` = 그 글이 공개돼 있는 언어 전부.
 * 🔴 옛 서버(TRAD-3921 backend 배포 전)에는 목록의 `lang` 과 `availableLangs` 가 없다 → `lang` 없으면 ko, `availableLangs` 없으면 `[lang]`.
 *    요청의 `?lang=` 도 옛 서버는 무시하고 한국어를 준다 → 영어 주소는 «한국어 글 + 안내 띠»(폴백)로 떨어진다. 어느 쪽이 먼저 배포돼도 깨지지 않는다.
 *
 * ~~`entities/finance-relations/lib/content-locale.ts`(11/5 — 화면 언어만 보고 «한국어 글 대신 보여 주는 중»을 판정)~~ 를 이 파일이 대체한다.
 * 그때는 글이 한국어뿐이라 화면 언어만 보면 됐다. 이제는 글마다 다르므로 «응답의 lang ≠ 화면 언어»로 판정한다.
 */
export type FinanceContentLang = 'ko' | 'en';

/** 원문 언어 — 지금 운영 글은 전부 한국어가 원문이다. 응답에 `lang` 이 없을 때(옛 서버 목록)의 값이기도 하다. */
export const FINANCE_CONTENT_SOURCE_LANG: FinanceContentLang = 'ko';

export const isFinanceContentLang = (value: unknown): value is FinanceContentLang => value === 'ko' || value === 'en';

/** 주소 → 글 언어. `/en/insights` · `/en/insights/…` 만 en, 그 밖은 전부 ko. */
export const financeContentRouteLang = (pathname: string): FinanceContentLang =>
  /^\/en\/insights(\/|$)/.test(pathname) ? 'en' : 'ko';

/**
 * 기기(브라우저) 언어 → 글 언어. 서버의 Accept-Language 규칙과 같다(계약 §1 ②: `ko*` → ko, 그 밖 → en).
 * 🔴 화면 언어(getLocale — 미지원 언어는 ko)와 일부러 다르다: 일본어 기기의 앱은 목록 제목을 영어로 받는다(앱이 기기 언어를 헤더로 보낸다).
 *    웹뷰 본문만 한국어면 목록과 본문이 갈린다(설계 §10 U4). 값이 없으면 원문(서버 규칙 ③).
 */
export const financeContentDeviceLang = (navigatorLanguage: string | null | undefined): FinanceContentLang => {
  const value = (navigatorLanguage ?? '').trim().toLowerCase();
  if (value === '') return FINANCE_CONTENT_SOURCE_LANG;
  return value.startsWith('ko') ? 'ko' : 'en';
};

/** 요청에 실을 `lang` — 앱 웹뷰는 기기 언어, 그 밖은 주소. */
export const resolveFinanceContentRequestLang = (
  pathname: string,
  embed: boolean,
  navigatorLanguage: string | null | undefined,
): FinanceContentLang => (embed ? financeContentDeviceLang(navigatorLanguage) : financeContentRouteLang(pathname));

/** 섹션 주소 — 목록 `/insights` · `/en/insights`. */
export const insightsBasePath = (lang: FinanceContentLang): string => (lang === 'en' ? '/en/insights' : '/insights');

/**
 * 메뉴(상단 내비·모바일 더보기)의 «인사이트» 링크 — 화면 언어가 영어면 영어 목록으로(설계 §3). 그 밖(한국어 · 모르는 값)은 한국어 목록.
 * 프리렌더(ko-KR)는 한국어 주소를 굽는다 → 산출물의 메뉴 링크는 종전과 같다. 영어 목록은 hreflang·sitemap 으로 알린다.
 */
export const insightsHomePath = (uiLocale: string): string => insightsBasePath(uiLocale === 'en' ? 'en' : 'ko');

/** 글 주소 — `/insights/<slug>` · `/en/insights/<slug>`. `lang` 생략 = 한국어 주소(종전 `financeContentPath(slug)` 와 같은 값). */
export const financeContentPath = (slug: string, lang: FinanceContentLang = 'ko'): string =>
  `${insightsBasePath(lang)}/${encodeURIComponent(slug)}`;

/** 관계 지도·표 주소. */
export const insightsRelationsPath = (lang: FinanceContentLang): string => `${insightsBasePath(lang)}/relations`;

interface LangFields {
  lang?: string | null;
  availableLangs?: readonly unknown[] | null;
}

/** 응답 → 실제로 내려 준 언어. 없거나 빈 값이면 ko(옛 서버 목록). 모르는 값(`ja` 등)은 그대로 돌려준다 — «내 언어가 아님»으로 다뤄야 하므로 ko 로 뭉개지 않는다. */
export const servedLangOf = (item: LangFields): string => {
  const value = typeof item.lang === 'string' ? item.lang.trim().toLowerCase() : '';
  return value === '' ? FINANCE_CONTENT_SOURCE_LANG : value;
};

/** 응답 → 공개돼 있는 언어. 없으면(옛 서버) `[lang]`. */
export const availableLangsOf = (item: LangFields): string[] => {
  const raw = Array.isArray(item.availableLangs)
    ? item.availableLangs.flatMap(v => (typeof v === 'string' && v.trim() !== '' ? [v.trim().toLowerCase()] : []))
    : [];
  return raw.length > 0 ? [...new Set(raw)] : [servedLangOf(item)];
};

/**
 * 글 영역(제목·요약·표지·본문)에 달 `lang` 속성. 내려 준 언어가 화면 언어와 같으면 `undefined`(문서 `<html lang>` 과 같아 달 필요가 없다 —
 * 한국어 화면·프리렌더 산출물의 HTML 은 종전과 같다), 다르면 그 언어(스크린리더·검색엔진이 영어 화면 안의 한국어 구간을 알게).
 */
export const financeContentLangAttr = (servedLang: string, uiLocale: string): string | undefined =>
  servedLang === uiLocale ? undefined : servedLang;

/**
 * 글 위 안내 띠의 종류.
 *  - `none` — 내려 준 언어 = 화면 언어.
 *  - `available` — 화면 언어판이 «다른 주소»에 있다(한국어 주소를 영어 화면으로 연 경우) → 그 주소로 가는 링크(`lang`).
 *  - `korean_only` — 화면 언어판이 없어 한국어 글을 대신 보여 주는 중(11/5 «정석 폴백») → 사이트 언어 전환 버튼.
 *  - `other_language` — 한국어도 화면 언어도 아닌 글(모르는 `lang` 값 · 영어가 원문인 글을 한국어 화면에서) → 안내만.
 */
export type FinanceContentNotice =
  | { kind: 'none' }
  | { kind: 'available'; lang: FinanceContentLang }
  | { kind: 'korean_only' }
  | { kind: 'other_language' };

/**
 * 상세의 안내 띠. 자동으로 다른 주소로 넘기지 않는다(공유 링크는 보낸 사람이 본 언어 그대로 열려야 한다) — 안내 + 링크/버튼으로만.
 * 🔸 1차: 한국어 화면에서 한국어 글을 보는 중이면 영어판이 있어도 아무것도 띄우지 않는다(`servedLang === uiLocale` → none).
 */
export const resolveFinanceContentNotice = (
  uiLocale: string,
  servedLang: string,
  availableLangs: readonly string[],
): FinanceContentNotice => {
  if (servedLang === uiLocale) return { kind: 'none' };
  if (isFinanceContentLang(uiLocale) && availableLangs.includes(uiLocale)) return { kind: 'available', lang: uiLocale };
  return servedLang === 'ko' ? { kind: 'korean_only' } : { kind: 'other_language' };
};

/**
 * 목록의 안내 띠 — 항목마다 언어가 다를 수 있다(영어판이 있는 글만 영어로 온다).
 *  - `available` — 한국어 주소를 영어 화면으로 열었고, 영어판이 있는 글이 하나라도 있다 → 영어 목록 주소로 가는 링크.
 *  - `korean_only` — 화면 언어가 아닌 글이 있다. `all` = 전부(종전 문구 «These articles…») / 일부(«Some articles…»).
 */
export type FinanceContentListNotice =
  | { kind: 'none' }
  | { kind: 'available'; lang: FinanceContentLang }
  | { kind: 'korean_only'; all: boolean }
  | { kind: 'other_language' };

export const resolveFinanceContentListNotice = (uiLocale: string, items: readonly LangFields[]): FinanceContentListNotice => {
  const mismatched = items.filter(item => servedLangOf(item) !== uiLocale);
  if (mismatched.length === 0) return { kind: 'none' };
  if (isFinanceContentLang(uiLocale) && mismatched.some(item => availableLangsOf(item).includes(uiLocale))) {
    return { kind: 'available', lang: uiLocale };
  }
  if (mismatched.some(item => servedLangOf(item) !== 'ko')) return { kind: 'other_language' };
  return { kind: 'korean_only', all: mismatched.length === items.length };
};

/**
 * 이 화면의 정식 주소(canonical · 공유 주소)의 언어. 영어를 요청해 영어판을 받았을 때만 en — 영어 주소에서 한국어 글로 폴백했으면
 * 정식 주소는 한국어 주소다(같은 글의 중복 주소를 색인시키지 않는다 · 설계 §3 표).
 */
export const financeContentCanonicalLang = (requestLang: FinanceContentLang, servedLang: string): FinanceContentLang =>
  requestLang === 'en' && servedLang === 'en' ? 'en' : 'ko';

export interface HreflangAlternate {
  hrefLang: string;
  path: string;
}

/**
 * hreflang 대체 주소 — 두 언어판이 «둘 다 공개»일 때만 낸다(없는 판을 선언하지 않는다 — TRAD-2728). 아니면 빈 배열.
 * x-default 는 영어 주소: 한국어도 영어도 아닌 독자는 서버·앱과 같은 규칙(ko 가 아니면 en)으로 영어판을 받는다.
 * `toPath` = 언어 → 그 언어판의 주소(글이면 `/insights/<slug>` · `/en/insights/<slug>`).
 */
export const financeContentAlternates = (
  availableLangs: readonly string[],
  toPath: (lang: FinanceContentLang) => string,
): HreflangAlternate[] =>
  availableLangs.includes('ko') && availableLangs.includes('en')
    ? [
        { hrefLang: 'ko', path: toPath('ko') },
        { hrefLang: 'en', path: toPath('en') },
        { hrefLang: 'x-default', path: toPath('en') },
      ]
    : [];

/** 섹션 화면(목록 · 관계 지도·표)의 대체 주소 — 두 주소가 항상 있다(화면 문자열은 카탈로그 두 언어). */
export const INSIGHTS_SECTION_LANGS: readonly FinanceContentLang[] = ['ko', 'en'];
